import React, { useState } from "react";
import { Input, Button, Select, DatePicker, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";

const { Option } = Select;

const WordCountRequestFilters = ({
    onSearch,
    filtersList,
    filters: parentFilters,
    onReset,
    loading,
}) => {
    const isMobile = useIsMobile();

    const initialFilters = Array.isArray(filtersList)
        ? filtersList.reduce((acc, filter) => {
            acc[filter] = "";
            return acc;
        }, {})
        : {};

    const [filters, setFilters] = useState(initialFilters);

    React.useEffect(() => {
        if (parentFilters && Object.keys(parentFilters).length === 0) {
            setFilters({});
        } else if (parentFilters) {
            setFilters(parentFilters);
        }
    }, [parentFilters]);

    const handleFilterChange = (key, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value,
        }));
    };

    const handleSearch = () => {
        const formattedFilters = { ...filters };

        if (filters.created_from) {
            formattedFilters.created_from = dayjs(filters.created_from).format("YYYY-MM-DD");
        }
        if (filters.created_to) {
            formattedFilters.created_to = dayjs(filters.created_to).format("YYYY-MM-DD");
        }

        // Clean up empty filters
        const cleanFilters = Object.fromEntries(
            Object.entries(formattedFilters).filter(([_, v]) => v !== "" && v !== undefined && v !== null)
        );

        onSearch(cleanFilters);
    };

    const handleReset = async () => {
        setFilters({});
        await onReset();
    };

    const renderFilter = (filter) => {
        const defaultConfig = {
            component: Input,
            props: {
                placeholder: `Filter by ${filter
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                    } `,
                style: { width: isMobile ? "80vw" : 200 },
                value: filters[filter],
                onChange: (e) => handleFilterChange(filter, e.target.value),
            },
        };

        const filterConfigs = {
            status: {
                component: Select,
                props: {
                    placeholder: "Filter by Status",
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    value: filters.status || undefined,
                    onChange: (value) => handleFilterChange("status", value),
                    allowClear: true,
                },
                options: [
                    { value: "PENDING", label: "Pending" },
                    { value: "APPROVED", label: "Approved" },
                    { value: "REJECTED", label: "Rejected" },
                ],
            },
            task_key: {
                component: Input,
                props: {
                    placeholder: "Filter by Order Key",
                    style: { width: isMobile ? "80vw" : 200 },
                    value: filters.task_key || "",
                    onChange: (e) => handleFilterChange("task_key", e.target.value),
                },
            },
            created_from: {
                component: DatePicker,
                props: {
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    placeholder: "Created From",
                    value: filters.created_from ? dayjs(filters.created_from) : null,
                    onChange: (value) => handleFilterChange("created_from", value),
                },
            },
            created_to: {
                component: DatePicker,
                props: {
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    placeholder: "Created To",
                    value: filters.created_to ? dayjs(filters.created_to) : null,
                    onChange: (value) => handleFilterChange("created_to", value),
                },
            },
        };

        if (!['status', 'task_key', 'created_from', 'created_to'].includes(filter)) return null;

        const config = filterConfigs[filter] || defaultConfig;
        const Component = config.component;

        if (config.options) {
            return (
                <Component {...config.props}>
                    {config.options.map(({ value, label }) => (
                        <Option key={value} value={value}>
                            {label}
                        </Option>
                    ))}
                </Component>
            );
        }

        return <Component {...config.props} />;
    };

    return (
        <div style={{ width: "100%", marginBottom: "20px" }}>
            <div
                style={{ background: "#eceff1", padding: "20px", borderRadius: "8px" }}>
                {loading ? (
                    <Spin />
                ) : (
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "28px",
                            alignItems: "flex-end",
                            width: "100%",
                        }}>
                        {filtersList?.map((filter) => (
                            <div key={filter}>
                                {renderFilter(filter)}
                            </div>
                        ))}

                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                                justifyContent: "flex-end",
                                alignContent: "flex-end",
                                alignItems: "flex-end",
                                position: "relative",
                                right: isMobile ? "0" : "10px",
                                flexDirection: isMobile ? "column" : "row",
                                width: "100%",
                            }}>
                            <Button
                                type="primary"
                                style={{
                                    width: isMobile ? "80vw" : 140,
                                    background: "#040724",
                                }}
                                icon={<SearchOutlined />}
                                onClick={handleSearch}>
                                Search
                            </Button>
                            <Button
                                style={{ width: isMobile ? "80vw" : 140 }}
                                type="default"
                                onClick={handleReset}>
                                Reset
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WordCountRequestFilters;
